// =============================================================================
// TemplateGallery — pick a public-page look from live mini-renders (S100 3.3)
// =============================================================================
// Jake: "Add a box somewhere on the screen that allows you to choose a template
// and then click 'preview'. Probably best if you put small previews of each
// template and a radio button to select."
//
// 🔴 THE THUMBNAILS ARE LIVE RENDERS OF THE REAL RECRUIT, NEVER STATIC IMAGES.
// A committed PNG goes stale the moment a template changes and NOTHING tells
// you — the picker would show a design that no longer exists and the person
// choosing would never know. A live render cannot lie. It is also zero new
// assets, no regeneration step, and it scales to however many templates the
// registry grows to. Do not "optimize" this into cached screenshots.
//
// 🔴 CHOOSING AND APPLYING ARE DIFFERENT VERBS. Selecting a radio mutates
// nothing — it only changes which preview is highlighted. A separate explicit
// "Use this look" commits through the template endpoint. Do not merge them:
// browsing eleven looks must not write eleven times to a live profile.
//
// ⚠ Thumbnails arrive as JSON `{ html }` from an authenticated fetch and are
// injected with `srcdoc`, because an <iframe src> cannot carry an Authorization
// header. Serving the preview as a document would have meant a token in the
// URL. Every frame is `sandbox`ed with no allow-scripts — the pages carry no
// JS, so nothing is lost and a template can never run script in this page.
//
// Options come from the SERVER REGISTRY via props. No template key is typed in
// this file — that property is what makes 11 -> 12 a server-only change.

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';

// Rendered at full desktop width, then scaled down. Keeps the mini-render
// faithful to the real page instead of triggering mobile breakpoints.
const FRAME_W = 1100;
const FRAME_H = 1400;
const THUMB_W = 200;
const SCALE = THUMB_W / FRAME_W;

function Thumb({ templateKey, label, selected, html, error, onSelect, onOpen }) {
  const boxRef = useRef(null);
  // The HTML for every template arrives in ONE batch request from the parent.
  // Eleven separate preview calls measured 2,425 ms to fill this gallery on
  // production (22 DB queries for one screen); the batch does it in two.
  // The iframe still mounts lazily — cheap, and it keeps eleven frames from
  // being constructed before anyone scrolls to the section.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || visible) return undefined;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { io.disconnect(); setVisible(true); }
    }, { rootMargin: '300px' });
    io.observe(el);
    // 🔴 IO is an OPTIMISATION HERE, NOT A GATE. OC found zero thumbnails on
    // production while the API was healthy; my jsdom test could never have
    // caught it because it STUBBED IntersectionObserver, so the real observer
    // was the one thing never exercised. If it does not fire for any reason,
    // paint anyway — a blank gallery is a worse failure than an eager one.
    const fallback = setTimeout(() => setVisible(true), 600);
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, [visible]);

  return (
    <div
      ref={boxRef}
      className={`rounded-lg border-2 overflow-hidden ${selected ? 'border-mid-green' : 'border-gray-200'}`}
    >
      <button
        type="button"
        onClick={() => onSelect(templateKey)}
        className="block w-full text-left"
        style={{ width: THUMB_W, height: Math.round(FRAME_H * SCALE), overflow: 'hidden', position: 'relative' }}
        title={`Select ${label}`}
      >
        {visible && html ? (
          <iframe
            title={`${label} preview`}
            srcDoc={html}
            sandbox=""
            scrolling="no"
            style={{
              width: FRAME_W, height: FRAME_H, border: 0,
              transform: `scale(${SCALE})`, transformOrigin: 'top left',
              position: 'absolute', top: 0, left: 0, pointerEvents: 'none',
            }}
          />
        ) : (
          <div className="w-full h-full bg-gray-50 px-2 text-center flex items-center justify-center">
            {error ? (
              <span className="text-xs text-red-700">Preview failed: {error}</span>
            ) : (
              /* 🔴 A tile with nothing to say is why a mid-paint screenshot got
                 reported as eleven broken thumbnails. The skeleton makes
                 "still working" visually distinct from "blank and broken". */
              <span className="w-full" aria-live="polite">
                <span className="block animate-pulse space-y-2">
                  <span className="block h-16 bg-gray-200 rounded" />
                  <span className="block h-2 bg-gray-200 rounded w-3/4 mx-auto" />
                  <span className="block h-2 bg-gray-200 rounded w-1/2 mx-auto" />
                </span>
                <span className="block mt-2 text-[0.65rem] text-silver-mute">
                  {html === undefined ? 'Loading preview…' : 'Rendering…'}
                </span>
              </span>
            )}
          </div>
        )}
      </button>
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-white border-t border-gray-100">
        <label className="flex items-center gap-1.5 text-xs font-bold text-hunter cursor-pointer">
          <input
            type="radio"
            checked={selected}
            onChange={() => onSelect(templateKey)}
            className="accent-mid-green"
          />
          {label}
        </label>
        <button
          type="button"
          onClick={() => onOpen(templateKey, label)}
          className="text-[0.65rem] font-bold text-mid-green hover:underline shrink-0"
        >
          Preview
        </button>
      </div>
    </div>
  );
}

export default function TemplateGallery({
  templates, value, previewUrl, onApply, heading, blurb,
}) {
  const [selected, setSelected] = useState(value || templates[0]?.key);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);   // { key, label, html }
  // 🔴 ONE request for every template. Eleven single-preview calls measured
  // 2,425 ms to fill this gallery on production — 22 DB queries for one
  // screen, on the screen whose whole job is COMPARING looks. The batch route
  // renders all of them from two queries off one loaded view.
  const [renders, setRenders] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // 🔴 COLLAPSED BY DEFAULT ON EVERY LOAD (Jake: "it's gotta go behind an
  // expand arrow... That can't be the top of their profile all the time").
  // Deliberately NOT persisted — not localStorage, not the row. A kid who
  // opened it once should not find it open forever.
  const [opened, setOpened] = useState(false);
  const fetched = useRef(false);

  useEffect(() => { if (value) setSelected(value); }, [value]);

  // 🔴 FETCH ON FIRST EXPAND, ONCE. Two reasons this is not a mount effect:
  //  1. Default-collapsed means a normal page load must cost ZERO preview
  //     requests. Fetching on mount would pay for a gallery nobody opened.
  //  2. IntersectionObserver CANNOT be the trigger here — a collapsed
  //     <details> subtree has no layout, so nothing ever intersects and the
  //     thumbnails would either never load or all fire at once on expand.
  // `fetched` guards it so collapse/re-expand never re-fetches.
  const ensureLoaded = useCallback(() => {
    if (fetched.current) return;
    fetched.current = true;
    api.get(`${previewUrl}/all`)
      .then((body) => setRenders(body?.renders || {}))
      .catch((err) => { setLoadError(err.body?.error || err.message); fetched.current = false; });
  }, [previewUrl]);

  // Full-size uses the html the batch already returned — same bytes, no second
  // round trip. Falls back to the single route if the batch has not landed yet.
  const openPreview = useCallback(async (key, label) => {
    if (renders?.[key]) { setModal({ key, label, html: renders[key] }); return; }
    setModal({ key, label, html: null });
    try {
      const body = await api.get(`${previewUrl}?template=${encodeURIComponent(key)}`);
      setModal({ key, label, html: body?.html || '' });
    } catch (err) {
      setModal(null);
      setError(err.body?.error || err.message);
    }
  }, [previewUrl, renders]);

  async function apply() {
    setApplying(true);
    setError(null);
    try {
      await onApply(selected);
    } catch (err) {
      setError(err.body?.error || err.message);
    } finally {
      setApplying(false);
    }
  }

  const dirty = selected !== value;
  // 🔴 THE COLLAPSED HEADER NAMES THE ACTIVE LOOK. A collapsed control that
  // hides which template is live just makes you expand it to answer "what is
  // he on?" — the same interruption the collapse exists to remove.
  const currentLabel = templates.find((t) => t.key === value)?.label
    || templates[0]?.label || 'not set';

  return (
    <details
      className="card"
      onToggle={(e) => { if (e.currentTarget.open) { setOpened(true); ensureLoaded(); } }}
    >
      {/* Native <details>/<summary>: zero JS for the toggle, and keyboard +
          screen-reader behaviour correct for free. list-none hides Safari's
          default marker so the arrow below is the only affordance. */}
      <summary className="flex items-center gap-2 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        {/* G2: the marker has to flip — a ▶ over open content reads as closed.
            Rotated rather than swapped so it animates. */}
        <span className={`text-silver-mute text-xs transition-transform ${opened ? 'rotate-90' : ''}`}>▶</span>
        <h2 className="font-display font-bold text-hunter">{heading}</h2>
        <span className="text-sm text-silver-mute">— {currentLabel}</span>
        {/* G3: the action sits ON the header row, not orphaned beneath it.
            Inside <summary>, so stopPropagation keeps a click from toggling. */}
        <span className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {error && <span className="text-xs text-red-700">{error}</span>}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); apply(); }}
            disabled={applying || !dirty}
            className="text-sm font-bold px-3 py-1.5 rounded bg-mid-green text-white disabled:opacity-40"
          >
            {applying ? 'Saving…' : dirty ? 'Use this look' : 'In use'}
          </button>
        </span>
      </summary>

      <div className="mt-3 space-y-3">
      {blurb && <p className="text-xs text-silver-mute">{blurb}</p>}
      {loadError && (
        <p className="text-xs text-red-700">Could not load previews: {loadError}</p>
      )}

      <div className="flex flex-wrap gap-3">
        {opened && templates.map(({ key, label }) => (
          <Thumb
            key={key}
            templateKey={key}
            label={label}
            selected={selected === key}
            html={renders?.[key]}
            error={loadError}
            onSelect={setSelected}
            onOpen={openPreview}
          />
        ))}
      </div>

      {modal && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setModal(null)}
        >
          <div className="bg-white rounded-lg w-full max-w-5xl h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
              <span className="font-display font-bold text-hunter">{modal.label}</span>
              <button type="button" onClick={() => setModal(null)} className="text-sm font-bold text-silver-mute hover:text-hunter">
                Close
              </button>
            </div>
            {modal.html === null ? (
              <div className="flex-1 flex items-center justify-center text-silver-mute text-sm">Loading…</div>
            ) : (
              <iframe title={`${modal.label} full preview`} srcDoc={modal.html} sandbox="" className="flex-1 w-full rounded-b-lg" style={{ border: 0 }} />
            )}
          </div>
        </div>
      )}
      </div>
    </details>
  );
}
